import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { type OrgTimezoneView } from '@apm/contracts';
import { apiFetch } from './http';

/**
 * Múi giờ của tổ chức cho mọi phép tính "hôm nay" ở giao diện — APM-TZ-i.
 *
 * ## Vì sao là một biến cấp module, không phải context
 *
 * "Hôm nay" được hỏi ở hàng chục chỗ, phần lớn là hàm thuần (`todayIso`,
 * `today`, `isOverdue`) được gọi từ trong `useMemo`, từ bộ lọc, từ giá trị mặc
 * định của form. Đưa múi giờ qua context nghĩa là đổi chữ ký của tất cả chúng
 * để chở một chuỗi mà cả phiên làm việc chỉ có một giá trị.
 *
 * Layout nạp nó một lần (`useOrgTimezoneSync`). Trước khi nạp xong thì dùng
 * múi giờ của trình duyệt — với người dùng ở Việt Nam hai thứ trùng nhau, và
 * nó vẫn đúng hơn UTC ở mọi nơi khác.
 *
 * ## Vì sao múi giờ **tổ chức**, không phải của trình duyệt
 *
 * Người mở app trên một máy đặt giờ khác (đi công tác, máy ảo CI) vẫn phải
 * thấy cùng một "hôm nay" với cả đội: việc nào quá hạn là sự thật của dự án,
 * không phải của cái máy đang xem.
 */

export const orgTimezoneKey = ['org', 'timezone'] as const;

export const orgTimezoneApi = {
  get: () => apiFetch<OrgTimezoneView>('/org/timezone'),
  set: (input: OrgTimezoneView) =>
    apiFetch<OrgTimezoneView>('/org/timezone', { method: 'PUT', body: input }),
};

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

let current: string | null = null;

/** Múi giờ đang dùng để tính "hôm nay". */
export function orgTimezone(): string {
  return current ?? browserTimezone();
}

/** Chỉ cho layout và test. */
export function setOrgTimezone(timezone: string | null): void {
  current = timezone;
}

/** Nạp múi giờ của tổ chức đang đăng nhập vào `orgTimezone()`. */
export function useOrgTimezoneSync(enabled: boolean): void {
  const query = useQuery({
    queryKey: orgTimezoneKey,
    queryFn: orgTimezoneApi.get,
    enabled,
    staleTime: 60 * 60_000,
  });

  useEffect(() => {
    if (query.data) setOrgTimezone(query.data.timezone);
  }, [query.data]);
}
